import { useState, useRef } from 'react';
import { useAppStore } from '../../store/appStore';
import { Landmark, CustomGesture } from '../../types';
import './CustomGestureTrainer.css';

export const CustomGestureTrainer = () => {
  const { customGestures, addCustomGesture, removeCustomGesture, setTrainingMode, isTrainingMode } = useAppStore();
  const [gestureName, setGestureName] = useState('');
  const [gestureDescription, setGestureDescription] = useState('');
  const [trainingData, setTrainingData] = useState<Landmark[][]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingCount, setRecordingCount] = useState(0);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const startTraining = () => {
    if (!gestureName.trim()) {
      alert('Please enter a gesture name');
      return;
    }
    setTrainingMode(true);
    setTrainingData([]);
    setRecordingCount(0);
  };

  const recordSample = (landmarks: Landmark[]) => {
    if (!isTrainingMode) return;
    
    setTrainingData(prev => [...prev, landmarks]);
    setRecordingCount(prev => prev + 1);
  };

  const startRecording = () => {
    setIsRecording(true);
    let count = 0;
    
    recordingIntervalRef.current = setInterval(() => {
      // This would be called with actual landmarks from the camera
      // For now, we'll trigger it manually
      count++;
      if (count >= 30) {
        stopRecording();
      }
    }, 100);
  };

  const stopRecording = () => {
    setIsRecording(false);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  };

  const saveGesture = () => {
    if (trainingData.length < 10) {
      alert('Please record at least 10 samples');
      return;
    }

    const newGesture: CustomGesture = {
      id: `custom_${Date.now()}`,
      name: gestureName,
      description: gestureDescription,
      trainingData: trainingData,
      accuracy: calculateAccuracy(trainingData),
      createdAt: Date.now()
    };

    addCustomGesture(newGesture);
    
    // Reset form
    setGestureName('');
    setGestureDescription('');
    setTrainingData([]);
    setRecordingCount(0);
    setTrainingMode(false);
    
    alert(`Gesture "${gestureName}" saved successfully!`);
  };

  const calculateAccuracy = (data: Landmark[][]): number => {
    // Simple consistency check - calculate variance
    if (data.length < 2) return 0.5;
    
    let totalVariance = 0;
    const numLandmarks = data[0].length;
    
    for (let i = 0; i < numLandmarks; i++) {
      const xValues = data.map(d => d[i].x);
      const yValues = data.map(d => d[i].y);
      
      const xMean = xValues.reduce((a, b) => a + b, 0) / xValues.length;
      const yMean = yValues.reduce((a, b) => a + b, 0) / yValues.length;
      
      const xVariance = xValues.reduce((sum, val) => sum + Math.pow(val - xMean, 2), 0) / xValues.length;
      const yVariance = yValues.reduce((sum, val) => sum + Math.pow(val - yMean, 2), 0) / yValues.length;
      
      totalVariance += xVariance + yVariance;
    }
    
    const avgVariance = totalVariance / (numLandmarks * 2);
    // Convert variance to accuracy (lower variance = higher accuracy)
    return Math.max(0.5, Math.min(0.99, 1 - avgVariance * 10));
  };

  const cancelTraining = () => {
    setTrainingMode(false);
    setTrainingData([]);
    setRecordingCount(0);
    setIsRecording(false);
  };

  const deleteGesture = (id: string) => {
    if (confirm('Are you sure you want to delete this gesture?')) {
      removeCustomGesture(id);
    }
  };

  return (
    <div className="custom-gesture-trainer">
      <h2>Custom Gesture Training</h2>
      
      {!isTrainingMode ? (
        <div className="training-form">
          <div className="form-group">
            <label>Gesture Name:</label>
            <input
              type="text"
              value={gestureName}
              onChange={(e) => setGestureName(e.target.value)}
              placeholder="e.g., Hello, Goodbye, Help"
            />
          </div>
          
          <div className="form-group">
            <label>Description (optional):</label>
            <textarea
              value={gestureDescription}
              onChange={(e) => setGestureDescription(e.target.value)}
              placeholder="Describe the gesture..."
              rows={3}
            />
          </div>
          
          <button onClick={startTraining} className="btn btn-primary">
            Start Training
          </button>
        </div>
      ) : (
        <div className="training-session">
          <h3>Training: {gestureName}</h3>
          <p>Samples recorded: {recordingCount} / 30 (minimum 10)</p>
          
          <div className="training-instructions">
            <p>Perform the gesture multiple times in front of the camera.</p>
            <p>Try different angles and distances for better accuracy.</p>
          </div>
          
          <div className="training-actions">
            <button 
              onClick={saveGesture} 
              className="btn btn-success"
              disabled={recordingCount < 10}
            >
              Save Gesture
            </button>
            <button onClick={cancelTraining} className="btn btn-secondary">
              Cancel
            </button>
          </div>
          
          <div className="training-progress">
            <div 
              className="progress-bar" 
              style={{ width: `${(recordingCount / 30) * 100}%` }}
            />
          </div>
        </div>
      )}
      
      <div className="custom-gestures-list">
        <h3>Your Custom Gestures ({customGestures.length})</h3>
        {customGestures.length === 0 ? (
          <p className="empty-state">No custom gestures yet. Create one above!</p>
        ) : (
          <div className="gestures-grid">
            {customGestures.map(gesture => (
              <div key={gesture.id} className="gesture-card">
                <h4>{gesture.name}</h4>
                {gesture.description && <p>{gesture.description}</p>}
                <div className="gesture-stats">
                  <span>Samples: {gesture.trainingData.length}</span>
                  <span>Accuracy: {((gesture.accuracy || 0) * 100).toFixed(0)}%</span>
                </div>
                <button 
                  onClick={() => deleteGesture(gesture.id)}
                  className="btn btn-danger btn-sm"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
